/**
 * The agent form itself: react-hook-form bound to the schema.
 *
 * Nothing here cascades — an agent's fields do not write one another the way a
 * trigger's source writes its grain — so the only thing attached is the
 * re-validation the status line depends on.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

import { type AgentRecord } from "@/shared/domain/agents";

import {
  emptyFormState,
  parseSavedAgent,
} from "../Agents/buildRequestTemplate";

import {
  makeAgentFormSchema,
  type AgentFormOutput,
  type AgentFormValues,
} from "./agentFormSchema";
import { useDraftSeededForm } from "./useDraftSeededForm";

export interface UseAgentFormOptions {
  /**
   * The stored agent an edit starts from. Absent on create, and while an edit
   * is still fetching — the form starts blank either way.
   */
  initial?: AgentRecord;
  /**
   * The skills the org offers, once they have arrived. The one rule the form
   * cannot state on its own, so it is handed in and the resolver rebuilt
   * around it rather than checked separately.
   */
  eligibleSkillIds?: ReadonlySet<string>;
}

export function useAgentForm({
  initial,
  eligibleSkillIds,
}: UseAgentFormOptions): UseFormReturn<
  AgentFormValues,
  unknown,
  AgentFormOutput
> {
  const resolver = React.useMemo(
    () => zodResolver(makeAgentFormSchema({ eligibleSkillIds })),
    [eligibleSkillIds],
  );
  const form = useForm<AgentFormValues, unknown, AgentFormOutput>({
    resolver,
    defaultValues:
      initial === undefined ? emptyFormState() : parseSavedAgent(initial),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  // A draft handed over from chat lands on top of whatever the form opened
  // on, once — the same decision the old editor made, written through `reset`
  // rather than a state setter.
  useDraftSeededForm(form, initial);

  const { subscribe, trigger } = form;

  React.useEffect(
    () =>
      // After the first submit, any change re-validates the whole form. With a
      // resolver, react-hook-form refreshes only the changed field's error, so
      // without this the cross-field rule on the kickoff message stays red
      // after a goal has been added, and the status line over-counts.
      subscribe({
        formState: { values: true, isSubmitted: true },
        callback: ({ name, isSubmitted }) => {
          if (isSubmitted === true && name !== undefined) {
            void trigger();
          }
        },
      }),
    [subscribe, trigger],
  );

  return form;
}
